import { component } from 'picoapp';
import Modal from 'utils/modal';
import Product from '@grafikr/shopify-typescript/type/Product';
import { subscribe as klaviyoSubscribe } from 'klaviyo-subscribe';

interface QuizConfig {
  klaviyoListId: string
  quizResults: number
  resultsPageUrl: string
  urlParam: string
}

export default component(async (node) => {
  const closeBtn = node.querySelector('[data-quiz-close-btn]');
  const backBtn = node.querySelector<HTMLElement>('[data-quiz-back-btn]');
  const screens = node.querySelectorAll<HTMLElement>('[data-screen]');
  const infoBoxes = Array.from(node.querySelectorAll<HTMLElement>('[data-info-box]'));
  const infoBoxBtns = node.querySelectorAll<HTMLElement>('[data-info-box-btn]');
  const selectionForm = node.querySelector<HTMLFormElement>('[data-selection-form]');
  const resultsForm = node.querySelector<HTMLFormElement>('[data-results-form]');
  const resultsFormBtn = node.querySelector<HTMLElement>('[data-results-form-btn]');
  const resultsBtn = node.querySelector<HTMLElement>('[data-results-btn]');

  const config: QuizConfig = JSON.parse(node.dataset.config);

  const {
    klaviyoListId, quizResults, resultsPageUrl, urlParam,
  } = config;

  let isInitialized = false;

  const fetchProductData = async (): Promise<Product[]> => {
    const response = await fetch('/collections/all?sections=quiz-data');
    const data = await response.json();
    const html = data['quiz-data'];

    const container = document.createElement('div');
    container.innerHTML = html;

    const productData = JSON.parse(container.querySelector('[data-product-data]').innerHTML);

    return productData;
  };

  const initScreens = () => {
    let currentScreen = 0;

    const changeScreen = (modifier: 1 | -1) => {
      currentScreen += modifier;

      if (currentScreen <= 0) {
        currentScreen = 0;
        backBtn.hidden = true;
      } else {
        backBtn.hidden = false;
      }

      screens.forEach((screen, index) => {
        const isCurrentScreen = index === currentScreen;
        screen.hidden = !isCurrentScreen;

        if (isCurrentScreen && currentScreen !== screens.length - 1) {
          const inputs = screen.querySelectorAll('input');

          inputs.forEach((input) => {
            input.checked = false;
          });
        }
      });
    };

    selectionForm.addEventListener('change', () => {
      setTimeout(() => {
        changeScreen(1);
      }, 300);
    });

    backBtn.addEventListener('click', () => changeScreen(-1));
  };

  const initInfoBoxes = () => {
    infoBoxBtns.forEach((btn) => {
      const { index } = btn.dataset;
      const infoBox = infoBoxes.find((box) => box.dataset.index === index);

      const handleClick = (event: MouseEvent) => {
        if (!infoBox.contains(event.target as HTMLElement)) {
          infoBox.hidden = true;
          document.removeEventListener('click', handleClick);
        }
      };

      btn.addEventListener('click', () => {
        infoBox.hidden = !infoBox.hidden;

        if (!infoBox.hidden) {
          setTimeout(() => {
            document.addEventListener('click', handleClick);
          }, 0);
        }
      });
    });
  };

  const initResults = () => {
    const getResults = async () => {
      const products = await fetchProductData();
      const formData = new FormData(selectionForm);

      const tags = Array.from(formData.entries()).map(([prefix, value]) => (
        `${prefix}${value}`.toLowerCase()
      ));

      const results = products.sort((a, b) => {
        const getMatchingTags = (product: Product) => (
          product.tags.split(', ').filter((tag) => (
            tags.includes(tag.toLowerCase())
          ))
        );

        return getMatchingTags(a).length > getMatchingTags(b).length ? -1 : 1;
      }).slice(0, quizResults);

      return results;
    };

    const goToResultsPage = (products: Product[]) => {
      const url = new URL(resultsPageUrl, window.location.origin);

      products.forEach((product) => {
        url.searchParams.append('handle', product.handle);
      });

      url.searchParams.set('view', 'quiz-results');

      window.location.href = url.toString();
    };

    const setBtnLoading = (btn: HTMLElement) => {
      const { width } = btn.getBoundingClientRect();
      btn.style.width = `${width}px`;
      btn.innerHTML = theme.icons.loading;
    };

    resultsForm.addEventListener('submit', async (event) => {
      event.preventDefault();

      setBtnLoading(resultsFormBtn);

      const products = await getResults();

      const formData = new FormData(resultsForm);
      const email = formData.get('email');
      const name = formData.get('name');

      await klaviyoSubscribe(
        klaviyoListId,
        email,
        { $first_name: name },
      );

      goToResultsPage(products);
    });

    resultsBtn.addEventListener('click', async () => {
      setBtnLoading(resultsBtn);

      const products = await getResults();
      goToResultsPage(products);
    });
  };

  const initQuiz = async () => {
    initScreens();
    initInfoBoxes();
    initResults();
  };

  const initModal = () => {
    const modal = Modal(node);

    modal.on('open', () => {
      document.documentElement.style.overflow = 'hidden';

      if (!isInitialized) {
        initQuiz();
        isInitialized = true;
      }
    });

    modal.on('close', () => {
      document.documentElement.style.overflow = '';
    });

    document.addEventListener('quiz:open', () => modal.open());
    closeBtn.addEventListener('click', () => modal.close());

    const urlSearchParams = new URLSearchParams(window.location.search);

    if (urlSearchParams.get(urlParam) !== null) {
      modal.open();
    }
  };

  const init = () => {
    initModal();
  };

  init();
});
